import moment from 'moment';
import { IOrganizationItem } from '@/types/organization';
import { IUserItem } from '@/types/user';
import {
  type ICustomMilestone,
  type IPlan,
  type IMilestone,
  type IPayments,
  type ITemCondition,
  ChoicesBillingFrequencyPlan,
  ChoicesPaymentConditionPlan,
  PaymentType,
  PaymentStatus,
} from '@/types/order-shared.types';
import {
  type IPrice,
  type IPriceTier,
  type PricePhase,
  type ICustomPrice,
  type IGroupedPrice,
  ChoicesPeriodType,
} from '@/types/order-pricing.types';

// Re-export shared pricing types for backward compatibility
export { ChoicesPeriodType } from '@/types/order-pricing.types';
export type {
  IPrice,
  IPriceTier,
  PricePhase,
  ICustomPrice,
  IGroupedPrice,
} from '@/types/order-pricing.types';

// Re-export shared order types for backward compatibility
export {
  ChoicesBillingFrequencyPlan,
  ChoicesPaymentConditionPlan,
  PaymentType,
  PaymentStatus,
} from '@/types/order-shared.types';
export type {
  ICustomMilestone,
  IPlan,
  IMilestone,
  IPayments,
  ITemCondition,
} from '@/types/order-shared.types';

export enum ChoicesOrderUnitMeasure {
  quantity = 'quantity',
  percentageAmount = 'percentageAmount',
  amount = 'amount',
}

export enum ChoicesTypePlan {
  unspecified = 'unspecified',
  free = 'free',
  trial = 'trial',
  grace = 'grace',
  commitment = 'commitment',
  flexible = 'flexible',
  offline = 'offline',
  spot = 'spot',
}

// Specifies types of operations that can be applied to an order item.
export enum ChoicesOrderItemType {
  newResource = 'newResource', // Represents a new subscription.
  upgrade = 'upgrade', // Represents upgrading an existing subscription.
  addLicenses = 'addLicenses', // Represents adding additional licenses.
  renewal = 'renewal', // Represents renewing an existing subscription.
}

// Defines the types of order items that are relevant for billing processes.
export const ChoicesOrderItemTypeForBilling = {
  ...ChoicesOrderItemType,
  billingCloud: 'billingCloud', // Special type for cloud consumption items.
} as const;

// Represents the structure of an order including categorized items based on their action types.
export interface IOrderItems {
  items: IOrderItem[];
  itemsToRenewal: IOrderItem[];
  itemsToUpgrade: IOrderItem[];
  itemsToAddLicenses: IOrderItem[];
}

export interface IExtendedPriceTier extends Omit<IPriceTier, 'price'> {
  price: {
    finalPrice?: number;
    costPrice?: number;
    margin?: number;
  };
}
export interface IPhaseGroup extends Omit<IGroupedPrice, 'price' | 'priceTiers'> {
  firstPeriod?: number;
  lastPeriod?: number;
  price?: {
    finalPrice?: number;
    costPrice?: number;
    margin?: number;
  };
  priceTiers?: IExtendedPriceTier[];
}

// Describes an offer associated with an order item.
export interface IOffer {
  name: string; // Name of the offer.
  offerType: string; // Type of the offer.
  resourceType: string; // Resource type involved.
  hasPhases: boolean; // Indicates if the offer includes phases.
  pricePhases: PricePhase[]; // Price phases if applicable.
  endTime?: string; // Optional end time of the offer.
  startTime?: string; // Optional start time of the offer.
  price: IPrice; // Optional base price of the offer.
}

// Details a subscription, potentially including custom pricing and terms.
export interface ISubscription {
  _id: string; // Unique identifier for the subscription.
  plan: IPlan; // Associated plan.
  offer: IOffer; // Associated offer.
  customerTermCondition: ITemCondition; // Customer-specific terms.
  customerBillingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency for the customer.
  customTerm: ITemCondition; // Custom terms for the subscription.
  provisionDate: moment.Moment; // Start date of the subscription.
  renewalDate: string; // Standard end date of the subscription.
  renewalDateOperational: string; // Operational end date used for processing.

  usedQuantity: number; // Quantity of the subscription that's been used.
  purchaseQuantity: number; // Quantity of the subscription that's been purchased.

  hasPhases: boolean; // Indicates if there are multiple pricing phases.
  pricePhases: PricePhase[]; // Price phases if applicable.
  hasCustomPrice: boolean; // Indicates if there is custom pricing.
  customPrice: ICustomPrice; // Custom pricing details.
  price?: IPrice; // Standard price if no custom pricing.
  subscription: any; // Associated subscription if exists.
  organization: IOrganizationItem;
}

// Describes a billing milestone within a subscription or order item.
export interface IBillingMilestone {
  date: string; // Date of the milestone.
  period: number; // Period associated with this milestone.
  price: number; // Price at this milestone.
  quantity: number; // Quantity associated with this milestone.
  type: string; // Type of milestone.
  description: string; // Description of the milestone.
}

// Represents an order item within a larger order.
export interface IOrderItem {
  _id: string; // Unique identifier for the item.
  tempId: string; // Temporary identifier for processing.
  // Unused variables
  organization: any;
  partner: any;
  domain: any;
  provider: any;
  product: any;
  sku: any;
  payFromBag: boolean;
  settingType: string;
  additionalDocuments: any;
  maintainPrevious: any;
  autoRenewal: any;
  order: any;
  // End of unused variables

  orderItemType: ChoicesOrderItemType; // Type of the order item.
  quantity: number; // Quantity after processing.
  customPrice: ICustomPrice; // Custom pricing details.
  offer: IOffer; // Associated offer.
  plan: IPlan; // Associated plan.

  provisionDate: moment.Moment; // Provision date for the item.
  billingFrequency: ChoicesBillingFrequencyPlan; // Billing frequency.
  paymentCondition: ChoicesPaymentConditionPlan; // Payment condition.
  billingMilestones: IBillingMilestone[]; // Array of billing milestones.
  oldSubscription: ISubscription;
  subscription: ISubscription; // Associated subscription if exists.

  customizeBillingMilestones: boolean;
  numberOfMilestones: number;
  customMilestones: ICustomMilestone[];
  finalCustomMilestones: IMilestone[];

  approvalResponsibles?: IUserItem[];
  provisionResponsibles?: IUserItem[];

  isAlignment?: boolean; // Whether alignment mode is enabled.
  customTermEndDate?: string | null; // Custom term end date for alignment.
  alignedSubscription?: string | null; // ID of the aligned subscription.

  summary: any;
  syncErrorMessage?: string;
}

// Represents the state structure used within a specific context, managing order items.
export interface OrderState {
  orderItems: IOrderItem[]; // Array of order items managed by the state.
}

export enum BillingCycleType {
  MONTHLY = 'MONTHLY',
  ANNUAL = 'ANNUAL',
}

export enum PlanType {
  COMMITMENT = 'commitment',
  TRIAL = 'trial',
  OFFLINE = 'offline',
}

export interface Payments {
  paymentPeriod: number;
  paymentDate: moment.Moment;
  startPeriod: moment.Moment;
  endPeriod: moment.Moment;
  proportionalDays: number;

  price: number; // Base price before adjustments.
  costPrice: number; // Cost price to the provider.
  discount: number; // Discount amount applied.
  finalPrice: number; // Final price after all adjustments.
  margin: number; // Profit margin.
  amount: number;

  subtotal: number;
  totalDiscount: number;
  total: number;

  type: PaymentType;
  status: PaymentStatus;
  // paymentPrice: number;
}

export interface IOrderItemSummary {
  orderItemType: ChoicesOrderItemType;
  provisionDate: string;
  provisionToday: boolean;
  provision: IProvision;
  subscription: ISummarySubscription;
}

export interface ISummarySubscription {
  quantity: number;
}

export interface IProvision {
  quantity: number;
  planType: ChoicesTypePlan;
  termCondition: ITermCondition;
  billingFrequency: ChoicesBillingFrequencyPlan;
  startDate: moment.Moment;
  endDate: moment.Moment;
  endBillingCicle: moment.Moment;
  period: number;
  payments: IPayments[];
  milestones: IMilestone[];
}

export interface ITermCondition {
  _id: string;
  name: string;
  code: string;
  amount: number;
  unit: moment.unitOfTime.DurationConstructor;
}
